import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Expand, X } from 'lucide-react';

// Every angle we hold for a product, card thumbnail first.
export const anglesFor = (item) =>
  item.images && item.images.length > 0 ? item.images : [item.image].filter(Boolean);

const Lightbox = ({ item, photos, index, setIndex, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const count = photos.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count, setIndex]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count, setIndex]);

  // Keyboard: escape closes, arrows move, tab stays inside the dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowRight' && count > 1) { e.preventDefault(); next(); return; }
      if (e.key === 'ArrowLeft' && count > 1) { e.preventDefault(); prev(); return; }
      if (e.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll('button');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [count, next, prev, onClose]);

  // Lock background scroll while open, and land focus inside the dialog.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col motion-safe:animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-label={`${item.name} — photo ${index + 1} of ${count}`}
      ref={dialogRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="lightbox"
    >
      {/* Bar */}
      <div className="flex items-start justify-between gap-4 px-5 py-4 text-white">
        <div className="min-w-0">
          <p className="font-bold text-cyan-400 truncate">{item.name}</p>
          {item.finish && (
            <p className="text-sm text-gray-200" data-testid="lightbox-finish">
              Finish: <span className="font-semibold">{item.finish}</span>
            </p>
          )}
          <p className="text-xs text-gray-400">
            {count > 1 ? `Angle ${index + 1} of ${count}` : 'Full size'}
            {item.price ? ` · ${item.price}` : ''}
          </p>
        </div>
        <button
          type="button"
          ref={closeRef}
          onClick={onClose}
          aria-label="Close photo viewer"
          data-testid="lightbox-close"
          className="flex-shrink-0 rounded-full border border-slate-600 p-2 text-gray-300 hover:text-white hover:border-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
        >
          <X size={20} />
        </button>
      </div>

      {/* Stage */}
      <div className="relative flex-1 flex items-center justify-center px-4 pb-2 min-h-0">
        {count > 1 && (
          <button
            type="button"
            onClick={prev}
            aria-label="Previous angle"
            className="absolute left-2 sm:left-6 z-10 rounded-full bg-black/70 border border-slate-600 p-3 text-gray-200 hover:text-white hover:border-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            <ChevronLeft size={24} />
          </button>
        )}

        <img
          src={photos[index]}
          alt={`${item.name}${item.finish ? `, ${item.finish} finish` : ''} — angle ${index + 1} of ${count}`}
          className="max-h-full max-w-full object-contain select-none"
          data-testid="lightbox-image"
        />

        {count > 1 && (
          <button
            type="button"
            onClick={next}
            aria-label="Next angle"
            className="absolute right-2 sm:right-6 z-10 rounded-full bg-black/70 border border-slate-600 p-3 text-gray-200 hover:text-white hover:border-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {/* Angle strip */}
      {count > 1 && (
        <div className="flex gap-2 overflow-x-auto px-5 py-4 justify-start sm:justify-center">
          {photos.map((src, i) => (
            <button
              type="button"
              key={src + i}
              onClick={() => setIndex(i)}
              aria-label={`Show angle ${i + 1}`}
              aria-current={i === index}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
                i === index ? 'border-cyan-400' : 'border-slate-700 hover:border-slate-500'
              }`}
            >
              <img src={src} alt="" loading="lazy" className="w-full h-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Thumbnail that opens the lightbox. `className` sizes the frame.
const ProductGallery = ({ item, className = 'h-64' }) => {
  const photos = anglesFor(item);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const triggerRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  if (photos.length === 0) return null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => { setIndex(0); setOpen(true); }}
        aria-label={`Enlarge ${item.name}${photos.length > 1 ? ` — ${photos.length} angles` : ''}`}
        data-testid={`gallery-open-${item.id}`}
        className={`group/img relative w-full ${className} bg-slate-900 overflow-hidden flex items-center justify-center p-4 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400`}
      >
        <img
          src={photos[0]}
          alt={`${item.name}${item.finish ? `, ${item.finish} finish` : ''}`}
          loading="lazy"
          className="max-h-full max-w-full object-contain motion-safe:transition-transform motion-safe:duration-500 group-hover/img:scale-105"
        />

        <span className="absolute top-2 right-2 rounded-full bg-black/70 border border-slate-600 p-1.5 text-cyan-400 opacity-0 group-hover/img:opacity-100 group-focus-visible/img:opacity-100 motion-safe:transition-opacity">
          <Expand className="w-3.5 h-3.5" aria-hidden="true" />
        </span>

        {photos.length > 1 && (
          <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-2.5 py-0.5 text-[11px] font-medium text-gray-300">
            {photos.length} angles
          </span>
        )}
      </button>

      {open && (
        <Lightbox
          item={item}
          photos={photos}
          index={index}
          setIndex={setIndex}
          onClose={close}
        />
      )}
    </>
  );
};

export default ProductGallery;
